import type {
  GenerateRequest,
  GenerateResponse,
  Proposal,
  ProposalStatus,
} from '@sab/core/schemas';

export interface ApiError {
  error: string;
  code: string;
}

class FetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(message);
    this.name = 'FetchError';
  }
}

async function request<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const body = init?.json !== undefined ? JSON.stringify(init.json) : (init?.body ?? null);
  const reqInit: RequestInit = {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    body,
  };
  if (init?.method) reqInit.method = init.method;
  const res = await fetch(path, reqInit);

  if (!res.ok) {
    let body: ApiError = { error: res.statusText, code: 'HTTP_ERROR' };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* ignore parse fail */
    }
    // Auto-redirect on auth miss (covers stale SPA cache after deploy).
    if (res.status === 401 && typeof window !== 'undefined') {
      const here = window.location.pathname + window.location.search;
      window.location.replace(`/proposal/login?redirect=${encodeURIComponent(here)}`);
      // Block further processing with a never-resolving promise.
      await new Promise<never>(() => {});
    }
    throw new FetchError(body.error, res.status, body);
  }

  return (await res.json()) as T;
}

export const api = {
  async me() {
    return request<{ email: string; environment: string; publicBaseUrl: string }>(
      '/proposal/api/me',
    );
  },
  /** Backend error log — drives the "Error Log" panel di /usage. */
  async errorLog(opts: { limit?: number; source?: string } = {}) {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.source) qs.set('source', opts.source);
    const q = qs.toString();
    return request<{
      entries: Array<{
        id: string;
        source: string;
        status: number | null;
        message: string;
        context: string | null;
        stack: string | null;
        actor_email: string | null;
        proposal_id: string | null;
        created_at: string;
      }>;
      count: number;
    }>(`/proposal/api/error-log${q ? `?${q}` : ''}`);
  },
  /** In-app usage monitor — drives <UpgradeBanner> + /usage page. */
  async usageSummary() {
    return request<{
      month: string;
      proposals: number;
      spendUSD: number;
      crawlMinutes: number;
      r2GB: number;
      anthropicReqLastMinute: number;
      limits: {
        browserMinutes: { limit: number; used: number; pct: number };
        anthropicRPM: { limit: number; used: number; pct: number };
        r2GB: { limit: number; used: number; pct: number };
      };
      upgradeNeeded: boolean;
      warnings: string[];
    }>('/proposal/api/usage-summary');
  },
  /**
   * Upload one brief screenshot. Returns the R2 key for use in
   * intake.briefImages. Multipart — bypasses the JSON `request()` helper.
   */
  async uploadBriefImage(
    file: File,
  ): Promise<{ key: string; contentType: string; bytes: number; name: string }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/proposal/api/upload-brief-image', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    if (!res.ok) {
      let body: ApiError = { error: res.statusText, code: 'HTTP_ERROR' };
      try {
        body = (await res.json()) as ApiError;
      } catch {
        /* ignore */
      }
      if (res.status === 401 && typeof window !== 'undefined') {
        const here = window.location.pathname + window.location.search;
        window.location.replace(`/proposal/login?redirect=${encodeURIComponent(here)}`);
        await new Promise<never>(() => {});
      }
      throw new FetchError(body.error, res.status, body);
    }
    return (await res.json()) as {
      key: string;
      contentType: string;
      bytes: number;
      name: string;
    };
  },
  /** Delete a brief screenshot before form submit. Idempotent. */
  async deleteBriefImage(key: string) {
    return request<{ ok: true }>('/proposal/api/brief-image', {
      method: 'DELETE',
      json: { key },
    });
  },
  /**
   * Auto-extract Intake fields from uploaded screenshots. Server reads R2,
   * sends to Claude Haiku 4.5 (primary) or Workers AI Llama Vision (fallback),
   * returns prefill data for the form. Caller decides which fields to apply.
   */
  async extractBrief(imageKeys: string[]) {
    return request<{
      extracted: {
        companyName?: string | null;
        contactName?: string | null;
        contactRole?: string | null;
        websiteUrl?: string | null;
        countryHint?: string | null;
        rawMessage?: string | null;
        confidence: 'high' | 'medium' | 'low';
      };
      modelUsed: string;
      imageCount: number;
    }>('/proposal/api/extract-brief', {
      method: 'POST',
      json: { imageKeys },
    });
  },
  async models() {
    return request<{
      defaultModelId: string;
      aiGatewayConfigured: boolean;
      models: Array<{
        id: string;
        label: string;
        tagline: string;
        provider: string;
        tier: string;
        approxCostUSD: number;
        available: boolean;
        secretEnv: string | null;
        getKeyUrl: string | null;
        secretCommand: string | null;
      }>;
    }>('/proposal/api/models');
  },
  async list(status?: ProposalStatus) {
    const qs = status ? `?status=${status}` : '';
    return request<{ items: ProposalListItem[] }>(`/proposal/api/proposals${qs}`);
  },
  async get(id: string) {
    return request<{ proposal: Proposal }>(`/proposal/api/proposals/${id}`);
  },
  async analyze(req: GenerateRequest) {
    return request<{
      analysis: GenerateResponse['analysis'];
      diagnostics: GenerateResponse['diagnostics'];
    }>('/proposal/api/analyze', {
      method: 'POST',
      json: req,
    });
  },
  async deck(payload: {
    intake: GenerateRequest['intake'];
    analysis: GenerateResponse['analysis'];
    modelId?: string;
  }) {
    return request<{
      slides: GenerateResponse['slides'];
      diagnostics: GenerateResponse['diagnostics'];
    }>('/proposal/api/deck', {
      method: 'POST',
      json: payload,
    });
  },
  async create(
    payload: Omit<
      Proposal,
      'id' | 'slug' | 'status' | 'createdAt' | 'updatedAt' | 'createdBy' | 'version'
    >,
  ) {
    return request<{ proposal: Proposal }>('/proposal/api/proposals', {
      method: 'POST',
      json: payload,
    });
  },
  async update(id: string, patch: Partial<Proposal>) {
    return request<{ proposal: Proposal }>(`/proposal/api/proposals/${id}`, {
      method: 'PATCH',
      json: patch,
    });
  },
  async regenerateSlide(id: string, slideIndex: number, instruction: string) {
    return request<{ proposal: Proposal; undoAvailable: number }>(
      `/proposal/api/proposals/${id}/regenerate-slide`,
      {
        method: 'POST',
        json: { slideIndex, instruction },
      },
    );
  },
  /**
   * Restore the most recent prior version of a slide. Server keeps up to 3
   * snapshots per (proposal, slide) and pops the latest on undo.
   * Returns the updated proposal + remaining undo budget for that slide.
   */
  async undoSlide(id: string, slideIndex: number) {
    return request<{ proposal: Proposal; undoAvailable: number }>(
      `/proposal/api/proposals/${id}/undo-slide`,
      {
        method: 'POST',
        json: { slideIndex },
      },
    );
  },
  /**
   * Returns a map of { [slideIndex]: undoAvailable } so the editor can show
   * the correct undo pill on initial load.
   */
  async slideHistorySummary(id: string) {
    return request<{ countsByIndex: Record<number, number> }>(
      `/proposal/api/proposals/${id}/slide-history`,
    );
  },
  /** Reorder slides — accepts the new order as array of slide IDs. */
  async reorderSlides(id: string, order: string[]) {
    return request<{ proposal: Proposal }>(`/proposal/api/proposals/${id}/slides/reorder`, {
      method: 'POST',
      json: { order },
    });
  },
  /** Delete one slide; server forbids deleting the last remaining slide. */
  async deleteSlide(id: string, slideId: string) {
    return request<{ proposal: Proposal }>(`/proposal/api/proposals/${id}/slides/${slideId}`, {
      method: 'DELETE',
    });
  },
  /**
   * Insert a new (empty) slide after the given index (or at index 0 if null).
   * Server fills sensible default headline based on type.
   */
  async insertSlide(id: string, afterIndex: number | null, type: string, headline?: string) {
    return request<{ proposal: Proposal; insertedAt: number }>(
      `/proposal/api/proposals/${id}/slides/insert`,
      {
        method: 'POST',
        json: { afterIndex, type, headline },
      },
    );
  },
  async publish(id: string, slug?: string) {
    return request<{ proposal: Proposal; publicUrl: string }>(
      `/proposal/api/proposals/${id}/publish`,
      {
        method: 'POST',
        json: slug ? { slug } : {},
      },
    );
  },
  async archive(id: string) {
    return request<{ ok: true }>(`/proposal/api/proposals/${id}/archive`, {
      method: 'POST',
    });
  },
};

export interface ProposalListItem {
  id: string;
  slug: string;
  status: ProposalStatus;
  companyName: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export { FetchError };
